import React, { useState, useEffect, useRef } from 'react'
import { Button, Input, Layout, Menu, theme } from 'antd'
import { Footer, Header } from 'antd/es/layout/layout';
import { ConsoleSqlOutlined, ContactsOutlined, EnvironmentOutlined, MailOutlined, PhoneOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { DesktopOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createNewsletter } from '../redux/actions/newsletter';
import { message } from 'antd';
import { RootState } from '../redux/store';
import SocialButtons from '../components/SocialButtons';

const MainLayout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const { loading } = useSelector((state: RootState) => state.newsletter);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, [location]);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const items = [
    {
      parent: {
        key: "/home",
        icon: <DesktopOutlined />,
        label: 'Anasayfa',
      }
    },
    {
      parent: {
        key: "/blog",
        icon: <FileTextOutlined />,
        label: 'Blog',
      }
    },
    {
      parent: {
        key: "/products",
        icon: <ConsoleSqlOutlined />,
        label: 'Ürünler'
      }
    },
    {
      parent: {
        key: "/iletisim",
        icon: <ContactsOutlined />,
        label: 'İletişim',
      }
    }
  ];

  const handleMenuSelect = (info: any) => {
    navigate(`${info.key}`);
  }

  const handleSubscribe = async () => {
    if (!email) {
      message.error('Lütfen email adresinizi giriniz');
      return;
    }

    try {
      await dispatch(createNewsletter(email));
      setEmail('');
    } catch (err: any) {
      message.error(err.response.data.message);
    }
  };

  return (
    <>
      <Layout className='min-h-screen flex flex-col'>
        <Header className='flex items-center drop-shadow-xl shadow-lg flex-wrap flex-grow-0' >
          <div ref={containerRef} className="rounded-lg flex-grow-0"><img src="/images/logo.png" alt="logo" className='w-32 md:w-40 lg:w-52 flex-grow-0 outline-none' /></div>
          <Menu
            theme="dark"
            mode="horizontal"
            onSelect={handleMenuSelect}
            selectedKeys={[location.pathname]}
            items={items.map(item => ({
              key: item.parent.key,
              icon: item.parent.icon,
              label: item.parent.label
            }))}
            className='flex-1 justify-center'
          />
          <div className='flex whitespace-nowrap flex-col items-center gap-2 text-white text-xl w-[178px] flex-grow-0'>
            <a href="tel:05438783435" className="hover:text-gray-300 transition-colors"><span><ClockCircleOutlined /> 0(543) 878-34-35</span></a>
            <a href="tel:05357455407" className="hover:text-gray-300 transition-colors"><span><ClockCircleOutlined /> 0(535) 745-54-07</span></a>
          </div>
        </Header>
        <SocialButtons />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            paddingBottom: '20px',
          }}
        >
          {children}
        </div>
        <Footer className="bg-[#0d3b16] text-white">
          <div className="container mx-auto px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Hakkımızda */}
              <div>
                <h3 className="text-xl font-bold mb-4">Hakkımızda</h3>
                <p className="text-gray-300">
                  Sakarya Lastik olarak 20 yılı aşkın süredir iş makinesi ve kamyon lastikleri konusunda uzman hizmet sunuyoruz.
                </p>
              </div>

              {/* Hızlı Menü */}
              <div>
                <h3 className="text-xl font-bold mb-4">Hızlı Menü</h3>
                <ul className="space-y-2">
                  <li><a href="/hakkimizda" className="text-gray-300 hover:text-white">Hakkımızda</a></li>
                  <li><a href="/products" className="text-gray-300 hover:text-white">Ürünler</a></li>
                  <li><a href="/blog" className="text-gray-300 hover:text-white">Blog</a></li>
                  <li><a href="/iletisim" className="text-gray-300 hover:text-white">İletişim</a></li>
                </ul>
              </div>

              {/* İletişim */}
              <div>
                <h3 className="text-xl font-bold mb-4">İletişim</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center gap-2">
                    <EnvironmentOutlined /> Adapazarı, Sakarya
                  </li>
                  <li className="flex items-center gap-2">
                    <a href="tel:05438783435" className="hover:text-gray-300 transition-colors"><PhoneOutlined /> 0(543) 878-34-35</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <a href="tel:05357455407" className="hover:text-gray-300 transition-colors"><PhoneOutlined /> 0(535) 745-54-07</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <MailOutlined /> info@sakaryalastik.com
                  </li>
                </ul>
              </div>

              {/* Çalışma Saatleri */}
              <div>
                <h3 className="text-xl font-bold mb-4">Çalışma Saatleri</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>Pazartesi - Cuma: 08:00 - 18:00</li>
                  <li>Cumartesi: 09:00 - 14:00</li>
                  <li>Pazar: Kapalı</li>
                </ul>
              </div>
            </div>

            {/* Newsletter */}
            <div className="mt-12 border-t border-[#135820] pt-8">
              <div className="max-w-md mx-auto">
                <h3 className="text-xl font-bold mb-4 text-center">Bültenimize Abone Olun</h3>
                <div className="flex gap-2">
                  <Input
                    placeholder="E-posta adresiniz"
                    className="flex-1"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    onPressEnter={handleSubscribe}
                    onBlur={handleSubscribe}
                    allowClear
                    type='email'
                    autoComplete='on'
                  />
                  <Button
                    type="primary"
                    className="bg-[#00861a] hover:bg-[#006d15]"
                    onClick={handleSubscribe}
                    loading={loading}
                  >
                    Abone Ol
                  </Button>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-8 text-center text-gray-400">
              <p>Sakarya Lastik ©{new Date().getFullYear()} Tüm hakları saklıdır.</p>
            </div>
          </div>
        </Footer>
        <div className="fixed bottom-0 flex w-full z-10 opacity-90 lg:hidden text-xl">
          <div className='w-1/2 flex flex-col justify-center items-center text-center bg-red-700 text-white p-1'>
            <a href='tel:05441205454'><span><PhoneOutlined className='rotate-180' /> Bizi Arayın</span>
            <span className='flex flex-col'>
              0544 120 5454
            </span></a>
          </div>
          <div className='w-1/2 flex flex-col justify-center items-center bg-green-800 text-white text-xl p-1 text-center'><a href={"https://wa.me/+905441205454"} target='_blank' rel="noreferrer"><WhatsAppOutlined /> Whatsapptan <br/>Bize Ulaşın</a></div>
        </div>
      </Layout>
    </>
  )
}

export default MainLayout